import React from "react";

// App
import { MILESTONE_STATUSES } from "./salesRoomData";

// UI components
import {
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const newMilestoneId = () =>
  `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Column labels, in grid order. The header carries them so the rows can be
// bare inputs; below the grid's comfortable width each field repeats its own
// label through the `data-label` attribute instead.
const COLUMNS = ["Due date", "Status", "Action", "Comments", "Owner"];

// Status labels, matching the buyer's chips. "pending" and "done" keep their
// original stored spellings so existing plans need no migration.
const STATUS_LABELS = {
  pending: "Not started",
  in_progress: "In progress",
  done: "Completed",
};

/**
 * Seller-side editor for the shared action plan. Steps are plain objects:
 * {id, title, description, due (unix ts or 0), assignee: seller|buyer,
 *  status: pending|in_progress|done}. Buyers report progress on any step
 * from the portal, and fully edit the ones they added themselves.
 */
export default function MilestoneBuilder(props) {
  const { milestones, setMilestones } = props;

  const update = (id, changes) => {
    setMilestones(
      milestones.map((milestone) =>
        milestone.id === id ? { ...milestone, ...changes } : milestone
      )
    );
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= milestones.length) {
      return;
    }
    const reordered = [...milestones];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setMilestones(reordered);
  };

  return (
    <div className="milestone-builder">
      {milestones.length === 0 && (
        <Typography owner="admin" variant="body2" className="mb-2">
          No steps yet. Build a shared action plan the buyer can follow -
          e.g. “Demo call”, “Proposal review”, “Contract signed”.
        </Typography>
      )}

      {milestones.length > 0 && (
        <div className="milestone-builder__head">
          {COLUMNS.map((column) => (
            <span key={column}>{column}</span>
          ))}
          <span />
        </div>
      )}

      {milestones.map((milestone, index) => (
        <div className="milestone-builder__row" key={milestone.id}>
          <div className="milestone-builder__field" data-label="Due date">
            <TextField
              fullWidth
              size="small"
              type="date"
              className="date-input-field"
              value={milestone.dueInput || ""}
              onChange={(e) =>
                update(milestone.id, { dueInput: e.target.value })
              }
            />
          </div>
          <div className="milestone-builder__field" data-label="Status">
            <TextField
              fullWidth
              select
              size="small"
              SelectProps={{ native: true }}
              value={milestone.status}
              onChange={(e) => update(milestone.id, { status: e.target.value })}
            >
              {MILESTONE_STATUSES.map((status) => (
                <option value={status} key={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </TextField>
          </div>
          <div className="milestone-builder__field" data-label="Action">
            <TextField
              fullWidth
              size="small"
              value={milestone.title}
              placeholder="What needs to happen?"
              onInput={(e) => update(milestone.id, { title: e.target.value })}
            />
          </div>
          <div className="milestone-builder__field" data-label="Comments">
            <TextField
              fullWidth
              size="small"
              value={milestone.description}
              placeholder="Optional"
              onInput={(e) =>
                update(milestone.id, { description: e.target.value })
              }
            />
          </div>
          <div className="milestone-builder__field" data-label="Owner">
            <TextField
              fullWidth
              select
              size="small"
              SelectProps={{ native: true }}
              value={milestone.assignee}
              onChange={(e) =>
                update(milestone.id, { assignee: e.target.value })
              }
            >
              <option value="seller">Us</option>
              <option value="buyer">Buyer</option>
            </TextField>
          </div>
          <div className="milestone-builder__tools">
            <Tooltip title="Move up">
              <span>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down">
              <span>
                <IconButton
                  size="small"
                  disabled={index === milestones.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete step">
              <IconButton
                size="small"
                onClick={() =>
                  setMilestones(milestones.filter((m) => m.id !== milestone.id))
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>

          {milestone.created_by === "buyer" && (
            <Typography
              owner="admin"
              variant="caption"
              className="milestone-builder__origin"
            >
              Added by {milestone.author || "the buyer"}
            </Typography>
          )}
        </div>
      ))}

      <Button
        variant="contained"
        color="secondary"
        className="action mt-2"
        size="small"
        sx={{ textTransform: "none" }}
        startIcon={<AddIcon />}
        onClick={() =>
          setMilestones([
            ...milestones,
            {
              id: newMilestoneId(),
              title: "",
              description: "",
              dueInput: "",
              assignee: "seller",
              status: "pending",
            },
          ])
        }
      >
        Add step
      </Button>
    </div>
  );
}
